import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { DownloadOutlined, GlobalOutlined, LinkOutlined } from '@ant-design/icons';
import { theme } from '../../styles/theme';
import { Button } from '../ui/Button';
import { ScrollReveal } from '../animations/ScrollReveal';
import { Row,Col} from 'antd';

const DownloadContainer = styled.div`
  padding: 80px 0 60px;
  background-color: ${theme.colors.white};

  @media (max-width: ${theme.breakpoints.md}) {
    padding: 60px 0 40px;
  }
`;

const DownloadWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const DownloadTitle = styled(motion.h2)`
  text-align: center;
  font-size: 36px;
  margin-bottom: 50px;
  color: ${theme.colors.text};
  font-weight: 700;

  @media (max-width: ${theme.breakpoints.md}) {
    font-size: 28px;
    margin-bottom: 30px;
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    font-size: 24px;
  }
`;



const DownloadCard = styled(motion.div)`
  background-color: ${theme.colors.background};
  padding: 30px;
  border-radius: 12px;
  text-align: center;
  box-shadow: ${theme.shadows.small};
  transition: all ${theme.transitions.default};
  border: 1px solid ${theme.colors.border};

  &:hover {
    transform: translateY(-5px);
    box-shadow: ${theme.shadows.medium};
    border-color: ${theme.colors.primary};
  }

  @media (max-width: ${theme.breakpoints.md}) {
    padding: 25px 20px;
  }
`;

const PlatformIcon = styled.div`
  font-size: 48px;
  color: ${theme.colors.primary};
  margin-bottom: 20px;
  transition: transform ${theme.transitions.default};

  ${DownloadCard}:hover & {
    transform: scale(1.1);
  }

  @media (max-width: ${theme.breakpoints.md}) {
    font-size: 40px;
  }
`;

const PlatformTitle = styled.h3`
  font-size: 24px;
  margin-bottom: 15px;
  color: ${theme.colors.text};
  font-weight: 600;

  @media (max-width: ${theme.breakpoints.md}) {
    font-size: 20px;
  }
`;

const PlatformDescription = styled.p`
  font-size: 16px;
  color: ${theme.colors.textSecondary};
  margin-bottom: 30px;
  line-height: 1.5;
  min-height: 48px;

  @media (max-width: ${theme.breakpoints.md}) {
    font-size: 14px;
    margin-bottom: 25px;
  }`

const SystemRequirements = styled.div`
  width: 100%;
  margin: 0 auto;
  margin-top: 60px;
  padding: 40px;
  background-color: ${theme.colors.background};
  border-radius: 8px;
  box-shadow: ${theme.shadows.small};
  transition: all ${theme.transitions.default};

  &:hover {
    box-shadow: ${theme.shadows.medium};
  }

  @media (max-width: ${theme.breakpoints.lg}) {
    margin-top: 50px;
    padding: 35px;
  }

  @media (max-width: ${theme.breakpoints.md}) {
    margin-top: 40px;
    padding: 30px 20px;
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    margin-top: 32px;
    padding: 20px 16px;
  }
`;

const RequirementsTitle = styled.h3`
  font-size: 20px;
  margin-bottom: 30px;
  color: ${theme.colors.text};
  text-align: center;
  font-weight: 600;

  @media (max-width: ${theme.breakpoints.lg}) {
    font-size: 19px;
    margin-bottom: 25px;
  }

  @media (max-width: ${theme.breakpoints.md}) {
    font-size: 18px;
    margin-bottom: 20px;
    text-align: left;
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    font-size: 16px;
    margin-bottom: 12px;
  }
`;

// 表格样式组件
const RequirementsTable = styled.div`
  width: 100%;
  margin-top: 16px;
  overflow-x: auto;
  border-radius: ${theme.borderRadius.sm};

  table {
    width: 100%;
    border-collapse: collapse;
    min-width: 600px;
    border-radius: 8px;
    overflow: hidden;
    background-color: ${theme.colors.white};
    transition: all ${theme.transitions.default};
    border: 1px solid ${theme.colors.border};
  }

  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${theme.colors.background};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${theme.colors.border};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${theme.colors.textSecondary};
  }

  @media (max-width: ${theme.breakpoints.lg}) {
    margin-top: 14px;
  }

  @media (max-width: ${theme.breakpoints.md}) {
    table {
      font-size: 14px;
    }
    margin-top: 12px;
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    margin-top: 10px;
  }
`;

const TableHeader = styled.th`
  background-color: ${theme.colors.primary};
  color: white;
  text-align: left;
  padding: 18px 24px;
  font-weight: 600;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid ${theme.colors.primaryHover};
  white-space: nowrap;

  @media (max-width: ${theme.breakpoints.lg}) {
    padding: 17px 22px;
    font-size: 13.5px;
  }

  @media (max-width: ${theme.breakpoints.md}) {
    padding: 14px 18px;
    font-size: 13px;
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    padding: 12px 16px;
    font-size: 12px;
  }
`;

const TableRow = styled.tr`
  transition: all ${theme.transitions.default};
  border-bottom: 1px solid ${theme.colors.border};

  &:nth-child(even) {
    background-color: ${theme.colors.background};
  }

  &:hover {
    background-color: ${theme.colors.primaryLight};
  }

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: ${theme.breakpoints.md}) {
    &:hover {
      background-color: inherit;
    }
  }
`;

const TableCell = styled.td`
  padding: 18px 24px;
  font-size: 16px;
  color: ${theme.colors.text};
  vertical-align: middle;

  @media (max-width: ${theme.breakpoints.lg}) {
    padding: 17px 22px;
    font-size: 15px;
  }

  @media (max-width: ${theme.breakpoints.md}) {
    padding: 14px 18px;
    font-size: 14px;
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    padding: 12px 16px;
    font-size: 13px;
  }
`;

const RequirementType = styled(TableCell)`
  font-weight: 600;
  min-width: 120px;
  color: ${theme.colors.primary};
  white-space: nowrap;
  font-family: inherit;

  @media (max-width: ${theme.breakpoints.sm}) {
    min-width: 100px;
  }
`;

export const Download: React.FC = () => {
  // 下载 ccproxy
  const handleDownloadCCproxy = () => {
    const pptUrl = "http://oss.xlvolution.cn/tools/ccproxysetup.exe";

    // 创建一个隐藏的a标签来触发下载
    const link = document.createElement("a");
    link.href = pptUrl;
    link.download = "ccproxy.exe"; // 设置下载文件名
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // 下载netbird
  const handleDownloadNetbird = () => {
    const pptUrl =
      "http://oss.xlvolution.cn/tools/netbird_installer_0.47.2_windows_amd64.exe";

    // 创建一个隐藏的a标签来触发下载
    const link = document.createElement("a");
    link.href = pptUrl;
    link.download = "netbird.exe"; // 设置下载文件名
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <DownloadContainer>
      <DownloadWrapper>
        <ScrollReveal>
          <DownloadTitle>代理下载</DownloadTitle>
        </ScrollReveal>

        <Row gutter={[30, 30]}>
          <Col xs={24} md={12} xl={12}>
            {/* ccproxy下载 */}
            <ScrollReveal delay={0.2}>
              <DownloadCard>
                <PlatformIcon>
                  <GlobalOutlined />
                </PlatformIcon>
                <PlatformTitle>ccproxy</PlatformTitle>
                <PlatformDescription>
                  下载后请联系客服进行配置
                </PlatformDescription>
                <Button
                  variant="primary"
                  size="large"
                  onClick={handleDownloadCCproxy}
                >
                  <DownloadOutlined /> 立即下载
                </Button>
              </DownloadCard>
            </ScrollReveal>
          </Col>
          <Col xs={24} md={12} xl={12}>
            {/* netbird下载 */}
            <ScrollReveal delay={0.2}>
              <DownloadCard>
                <PlatformIcon>
                  <LinkOutlined />
                </PlatformIcon>
                <PlatformTitle>netbird</PlatformTitle>
                <PlatformDescription>
                  下载后请联系客服进行配置
                </PlatformDescription>
                <Button
                  variant="primary"
                  size="large"
                  onClick={handleDownloadNetbird}
                >
                  <DownloadOutlined /> 立即下载
                </Button>
              </DownloadCard>
            </ScrollReveal>
          </Col>
        </Row>

        <ScrollReveal delay={0.5}>
          <SystemRequirements>
            <RequirementsTitle>
              请确保浏览器版本符合以下要求，以避免页面加载异常或功能无法使用：
            </RequirementsTitle>
            <RequirementsTable>
              <table>
                <thead>
                  <tr>
                    <TableHeader>浏览器</TableHeader>
                    <TableHeader>版本</TableHeader>
                  </tr>
                </thead>
                <tbody>
                  <TableRow>
                    <RequirementType>IE</RequirementType>
                    <TableCell>IE11（需要 polyfills）</TableCell>
                  </TableRow>
                  <TableRow>
                    <RequirementType>Edge</RequirementType>
                    <TableCell>最新两个版本</TableCell>
                  </TableRow>
                  <TableRow>
                    <RequirementType>Firefox</RequirementType>
                    <TableCell>最新两个版本</TableCell>
                  </TableRow>
                  <TableRow>
                    <RequirementType>Chrome</RequirementType>
                    <TableCell>最新两个版本</TableCell>
                  </TableRow>
                  <TableRow>
                    <RequirementType>Safari</RequirementType>
                    <TableCell>最新两个版本</TableCell>
                  </TableRow>
                  <TableRow>
                    <RequirementType>Opera</RequirementType>
                    <TableCell>最新两个版本</TableCell>
                  </TableRow>
                  <TableRow>
                    <RequirementType>Electron</RequirementType>
                    <TableCell>支持</TableCell>
                  </TableRow>
                </tbody>
              </table>
            </RequirementsTable>
          </SystemRequirements>
        </ScrollReveal>
      </DownloadWrapper>
    </DownloadContainer>
  );
};